// AladinnContextualChat.jsx - Version avec système d'onboarding et aide contextuelle

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { 
  RefreshCw, AlertCircle, Calendar, BarChart3, Users, Database, Target, Trophy, 
  Clock, Zap, Globe, FileText, Settings, Eye, EyeOff, X, BookOpen, PenTool,
  CheckCircle, HelpCircle, Bot, TrendingUp
} from 'lucide-react';
import { useAladinnData } from './useAladinnData';
import { chatContextManager } from './ChatContextManager';
import ChatInterface from './ChatInterface'; 
import { auth } from '../firebase';

// Import du header responsive
import ResponsiveHeader from './ResponsiveHeader';

// Import des composants améliorés
import ResultsManager from './ResultsManager';
import PlanningManager from './PlanningManager';
import SocialCommunityPanel from './SocialCommunityPanel';
import NotesManager from './NotesManager';

// Import des modals centralisées
import { SourcesModal } from './ChatComponents';
import { ObjectiveModal } from './ObjectiveModal';
import { ActionPlanModal } from './ActionPlanModal';
import ExplorationModal from './ExplorationModal';

// Import du gestionnaire de synthèse simplifiée
import { useStepSynthesis } from './StepSynthesisManager';

// ✨ NOUVEAUX IMPORTS - Onboarding et aide contextuelle
import WorkspaceWelcome from './WorkspaceWelcome';
import { 
  WorkspaceHelpButton, 
  WorkspaceInfoBanner,
  ChatModeIndicator,
  WorkspaceHelpFAB
} from './WorkspaceHelpSystem';
 
// Styles harmonisés avec améliorations responsive
const fontStyles = `
  .chat-title { 
    font-size: clamp(1.5rem, 4vw, 1.75rem); 
    line-height: 1.3; 
    font-weight: 700; 
  }
  .section-subtitle { 
    font-size: clamp(1.125rem, 3vw, 1.25rem); 
    line-height: 1.5; 
    font-weight: 600; 
  }
  .chat-text { 
    font-size: clamp(1rem, 2.5vw, 1.125rem); 
    line-height: 1.6; 
    font-weight: 400; 
  }
  .input-text { 
    font-size: clamp(1rem, 2.5vw, 1.125rem) !important; 
    line-height: 1.5 !important; 
  }
  .status-text { 
    font-size: clamp(0.875rem, 2vw, 1rem); 
    line-height: 1.5; 
  }
  .context-text { 
    font-size: clamp(0.875rem, 2vw, 1rem); 
    line-height: 1.5; 
    font-weight: 500; 
  }
  .button-text { 
    font-size: clamp(0.875rem, 2vw, 0.9375rem); 
    line-height: 1.4; 
    font-weight: 500; 
  }
  
  @media (max-width: 768px) {
    .workspace-container {
      padding: 0.5rem !important;
    }
    
    .workspace-content {
      margin: 0 !important;
      border-radius: 0.5rem !important;
    }
    
    .modal-content {
      margin: 1rem !important;
      max-height: 90vh !important;
      overflow-y: auto !important;
    }
    
    .responsive-grid {
      grid-template-columns: 1fr !important;
      gap: 0.75rem !important;
    }
    
    .touch-target {
      min-height: 44px !important;
      min-width: 44px !important;
    }
  }
  
  @media (max-width: 480px) {
    .workspace-container {
      padding: 0.25rem !important;
    }
    
    .compact-spacing {
      padding: 0.5rem !important;
      margin: 0.25rem !important;
    }
  }
`;

const additionalStyles = `
  .share-button-highlight:hover {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    transform: scale(1.05);
  }
  
  .action-button {
    transition: all 0.2s ease;
  }
  
  .action-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  @media (max-width: 768px) {
    .action-buttons {
      gap: 0.25rem !important;
    }
    
    .action-button {
      padding: 0.375rem !important;
    }
  }
  
  @keyframes slide-in-right {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  .animate-slide-in-right {
    animation: slide-in-right 0.3s ease-out;
  }
`;

// Composants de chargement et d'erreur
const LoadingComponent = () => (
  <div className="max-w-6xl mx-auto p-3 sm:p-6 w-full workspace-container">
    <style>{fontStyles}</style>
    <div className="w-full bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-lg border border-blue-200 p-4 sm:p-8 workspace-content">
      <div className="text-center">
        <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4">
          <div className="w-full h-full rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center">
            <RefreshCw className="h-6 w-6 sm:h-8 sm:w-8 text-white animate-spin" />
          </div>
        </div>
        <h3 className="chat-title text-blue-700 mb-4">Initialisation d'Aladinn Workspace</h3>
        <p className="chat-text text-blue-600">Chargement de votre espace de travail collaboratif...</p>
        <div className="mt-6 flex items-center justify-center gap-2">
          <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse delay-75"></div>
          <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse delay-150"></div>
        </div>
      </div>
    </div>
  </div>
);

const ErrorComponent = ({ error, onRetry, onGoBack }) => (
  <div className="max-w-6xl mx-auto p-3 sm:p-6 w-full workspace-container">
    <style>{fontStyles}</style>
    <div className="w-full bg-gradient-to-br from-red-50 to-pink-50 rounded-xl shadow-lg border border-red-200 p-4 sm:p-8 workspace-content">
      <div className="text-center">
        <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
          <AlertCircle className="h-6 w-6 sm:h-8 sm:w-8 text-red-500" />
        </div>
        <h3 className="chat-title text-red-700 mb-4">Erreur de connexion</h3>
        <p className="chat-text text-red-600 mb-6">{error}</p>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          <button 
            onClick={onRetry}
            className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-md hover:shadow-lg touch-target"
          >
            <div className="flex items-center justify-center gap-2">
              <RefreshCw size={16} />
              <span className="button-text">Réessayer</span>
            </div>
          </button>
          <button 
            onClick={onGoBack}
            className="w-full sm:w-auto px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors shadow-md hover:shadow-lg touch-target"
          >
            <span className="button-text">Retour</span>
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Composant principal
const AladinnContextualChat = () => {
  const { competenceId } = useParams();
  
  // États du workspace
  const [workspaceView, setWorkspaceView] = useState('chat');
  
  // États des modals centralisées
  const [showSourcesModal, setShowSourcesModal] = useState(false);
  const [showObjectiveModal, setShowObjectiveModal] = useState(false);
  const [showActionPlanModal, setShowActionPlanModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showExplorationModal, setShowExplorationModal] = useState(false); // 🆕 NOUVEAU - Modal d'exploration
  
  // ✨ NOUVEAUX ÉTATS - Onboarding et aide contextuelle
  const [showWelcome, setShowWelcome] = useState(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [currentChatMode, setCurrentChatMode] = useState('conversation'); // 'conversation' ou 'guided'
  
  // 🆕 NOUVEAU - Récupérer le userId depuis Firebase Auth
  const [userId, setUserId] = useState(null);
  
  // États locaux synchronisés avec Firebase
  const [localWeeklyPlanning, setLocalWeeklyPlanning] = useState([]);
  const [localActionPlan, setLocalActionPlan] = useState(null);
  const [planningVersion, setPlanningVersion] = useState(0);
  const [isDataReady, setIsDataReady] = useState(false);
  
  // États pour les résultats et la gamification
  const [userProgress, setUserProgress] = useState({
    totalPoints: 0,
    level: 1,
    dailyStreak: 0,
    weeklyGoal: 0,
    completedActions: 0
  });
  
  // États pour la planification quotidienne
  const [currentDayActions, setCurrentDayActions] = useState([]);
  
  // États pour la communauté sociale
  const [socialData, setSocialData] = useState({
    users: [],
    discussions: [],
    groups: [],
    events: [],
    collaborations: []
  });

  // États simplifiés pour les notifications
  const [feedbackNotifications, setFeedbackNotifications] = useState([]);
  const [notesTimestamp, setNotesTimestamp] = useState(Date.now());
  const [updatedRideaux, setUpdatedRideaux] = useState(new Set());
  
  // 🆕 NOUVEAU - Statistiques d'exploration (simulées pour le moment)
  const [explorationStats, setExplorationStats] = useState({
    documentsCount: 0,
    conceptsCount: 0
  });

  // Hook principal avec gestion centralisée de l'étape courante
  const {
    competenceData,
    userObjective,
    searchObjective,
    savedSources,
    actionPlan,
    currentStepId,
    changeCurrentStep,
    getCurrentStep,
    calculateDefaultCurrentStep,
    loading,
    error,
    connectionStatus,
    saveChatData,
    clearCache,
    
    saveWeeklyPlanning,
    saveElementaryAction,
    deleteElementaryAction,
    
    saveStepResults,
    updateStepStatus,
    
    saveStepNotes,
    updateStepNoteSection,
    getStepNotes,
    deleteStepNotes,
    getStepNotesStats,
    copyStepNotes,
    
    saveStepSynthesis,
    getStepSynthesis,
    deleteStepSynthesis,
    
    debugDataState
  } = useAladinnData(competenceId);

  // Hook de synthèse d'étape intégré
  const {
    synthesisData,
    generateSynthesisFromNotes
  } = useStepSynthesis(
    competenceData,
    currentStepId,
    getStepNotes
  );

  // ✨ NOUVEAU - Détection du premier utilisateur et affichage du welcome
  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('aladinn_workspace_welcome_seen');
    const hasNotes = competenceData?.stepNotes && Object.keys(competenceData.stepNotes).length > 0;
    const hasPlanning = competenceData?.weeklyPlanning && competenceData.weeklyPlanning.length > 0;
    
    // Afficher la bienvenue si c'est la première fois ET qu'il n'y a pas encore de données
    if (!hasSeenWelcome && !hasNotes && !hasPlanning && competenceData) {
      setIsFirstTimeUser(true);
      setShowWelcome(true);
    }
  }, [competenceData]);

  // 🆕 NOUVEAU - Initialiser le userId depuis Firebase Auth
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (currentUser?.uid) {
      console.log('UserId récupéré depuis Firebase Auth:', currentUser.uid);
      setUserId(currentUser.uid);
    } else {
      console.warn('Aucun utilisateur connecté trouvé dans Firebase Auth');
      setUserId(null);
    }
  }, []);

  // ✨ NOUVEAU - Gestion des modales d'onboarding et aide
  const handleCloseWelcome = useCallback(() => {
    setShowWelcome(false);
    setIsFirstTimeUser(false);
  }, []);

  const handleShowWelcome = useCallback(() => {
    setShowWelcome(true);
  }, []);

  const handleNavigateFromWelcome = useCallback((viewId) => {
    setWorkspaceView(viewId);
  }, []);

  // Callback quand une note est sauvegardée depuis le chat
  const handleNotesSaved = useCallback((stepId, rideauId) => {
    console.log('Note sauvegardée, mise à jour:', { stepId, rideauId });
    setNotesTimestamp(Date.now());
    setUpdatedRideaux(prev => new Set([...prev, rideauId]));
  }, []);

  // Réinitialiser les badges après 10 secondes
  useEffect(() => {
    if (updatedRideaux.size > 0) {
      const timer = setTimeout(() => {
        setUpdatedRideaux(new Set());
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [updatedRideaux]);

  // Gestionnaires pour le header responsive
  const handleViewChange = useCallback((newView) => {
    console.log('Vue changée:', newView);
    setWorkspaceView(newView);
  }, []);

  const handleQuickAccess = useCallback((accessId) => {
    console.log('Accès rapide:', accessId);
    
    switch (accessId) {
      case 'objective':
        setShowObjectiveModal(true);
        break;
      case 'actionplan':
        setShowActionPlanModal(true);
        break;
      case 'sources':
        setShowSourcesModal(true);
        break;
      case 'exploration':
        setShowExplorationModal(true);
        break;
      case 'welcome':
        handleShowWelcome();
        break;
      default:
        console.warn('Accès rapide non reconnu:', accessId);
    }
  }, [handleShowWelcome]);

  const handleOpenModal = useCallback((modalId) => {
    console.log('Ouverture modal:', modalId);
    
    switch (modalId) {
      case 'settings':
        setShowSettingsModal(true);
        break;
      case 'notifications':
        break;
      default:
        console.warn('Modal non reconnue:', modalId);
    }
  }, []);

  const handleNavigateToNotes = useCallback(() => {
    setWorkspaceView('notes');
  }, []);

  const hasNotifications = useMemo(() => {
    return (!currentStepId && actionPlan?.steps?.length > 0) || 
           (userProgress.completedActions > 0) ||
           (feedbackNotifications.length > 0) ||
           (updatedRideaux.size > 0);
  }, [currentStepId, actionPlan?.steps?.length, userProgress.completedActions, feedbackNotifications.length, updatedRideaux]);

  // Synchronisation des données principales
  useEffect(() => {
    console.log('Synchronisation des données principales:', {
      hasCompetenceData: !!competenceData,
      hasActionPlan: !!actionPlan,
      currentStepId,
      currentStepTitle: getCurrentStep()?.titre,
      weeklyPlanningFromCompetence: competenceData?.weeklyPlanning?.length || 0,
      connectionStatus,
      loading
    });

    if (actionPlan && (!localActionPlan || JSON.stringify(actionPlan) !== JSON.stringify(localActionPlan))) {
      console.log('Plan d\'action mis à jour localement');
      setLocalActionPlan(actionPlan);
    }

    const weeklyPlanningSource = competenceData?.weeklyPlanning;
    if (weeklyPlanningSource && Array.isArray(weeklyPlanningSource) && weeklyPlanningSource.length > 0) {
      const currentPlanningHash = JSON.stringify(localWeeklyPlanning);
      const newPlanningHash = JSON.stringify(weeklyPlanningSource);
      
      if (currentPlanningHash !== newPlanningHash) {
        console.log('Planning hebdomadaire mis à jour:', weeklyPlanningSource.length, 'semaines');
        setLocalWeeklyPlanning(weeklyPlanningSource);
        setPlanningVersion(prev => prev + 1);
      }
    }

    if (competenceData && connectionStatus === 'connected' && !loading) {
      setIsDataReady(true);
      console.log('Données synchronisées et prêtes');
    }

  }, [competenceData, actionPlan, currentStepId, getCurrentStep, connectionStatus, loading, localActionPlan, localWeeklyPlanning]);

  // Wrappers pour la gestion du planning
  const handleUpdatePlanning = useCallback(async (updatedPlanning) => {
    console.log('Mise à jour planning demandée:', updatedPlanning?.length, 'semaines');
    
    if (!saveWeeklyPlanning) {
      console.error('Fonction saveWeeklyPlanning non disponible');
      return false;
    }
    
    try {
      setLocalWeeklyPlanning(updatedPlanning);
      setPlanningVersion(prev => prev + 1);
      
      const result = await saveWeeklyPlanning(updatedPlanning);
      
      if (result) {
        console.log('Planning sauvegardé et synchronisé avec Firebase');
        return true;
      } else {
        console.error('Échec sauvegarde planning, rollback local');
        setLocalWeeklyPlanning(competenceData?.weeklyPlanning || []);
        return false;
      }
    } catch (error) {
      console.error('Erreur wrapper planning:', error);
      setLocalWeeklyPlanning(competenceData?.weeklyPlanning || []);
      return false;
    }
  }, [saveWeeklyPlanning, competenceData?.weeklyPlanning]);

  const handleUpdateElementaryAction = useCallback(async (actionId, actionData) => {
    console.log('Mise à jour action élémentaire:', actionId);
    
    if (!saveElementaryAction) {
      console.error('Fonction saveElementaryAction non disponible');
      return false;
    }
    
    try {
      const result = await saveElementaryAction(actionData);
      if (result) {
        console.log('Action élémentaire mise à jour en Firebase');
        
        setLocalWeeklyPlanning(prev => {
          return prev.map(week => ({
            ...week,
            elementaryActions: week.elementaryActions?.map(action =>
              action.id === actionId ? { ...action, ...actionData } : action
            ) || []
          }));
        });
      }
      return result;
    } catch (error) {
      console.error('Erreur mise à jour action:', error);
      return false;
    }
  }, [saveElementaryAction]);

  const handleDeleteElementaryAction = useCallback(async (actionId) => {
    console.log('Suppression action élémentaire:', actionId);
    
    if (!deleteElementaryAction) {
      console.error('Fonction deleteElementaryAction non disponible');
      return false;
    }
    
    try {
      const result = await deleteElementaryAction(actionId);
      if (result) {
        console.log('Action élémentaire supprimée de Firebase');
        
        setLocalWeeklyPlanning(prev => {
          return prev.map(week => ({
            ...week,
            elementaryActions: week.elementaryActions?.filter(action => action.id !== actionId) || []
          }));
        });
      }
      return result;
    } catch (error) {
      console.error('Erreur suppression action:', error);
      return false;
    }
  }, [deleteElementaryAction]);

  // Wrapper pour traiter les interactions guidées
  const handleGuidedInteraction = useCallback(async (interactionData) => {
    try {
      console.log('Traitement interaction guidée avec synchronisation');
      
      const notification = {
        id: Date.now(),
        type: 'guided_sync',
        message: 'Réponse synchronisée avec les notes',
        timestamp: new Date().toISOString()
      };
      
      setFeedbackNotifications(prev => [...prev.slice(-2), notification]);
      
      setTimeout(() => {
        setFeedbackNotifications(prev => prev.filter(n => n.id !== notification.id));
      }, 4000);
      
      generateSynthesisFromNotes();
      
      return true;
    } catch (error) {
      console.error('Erreur synchronisation interaction guidée:', error);
      return false;
    }
  }, [generateSynthesisFromNotes]);

  // Wrapper pour la sauvegarde des notes avec notification
  const handleSaveStepNoteEnhanced = useCallback(async (noteData) => {
    console.log('Sauvegarde note avec coordination:', noteData.sectionId);
    
    if (!updateStepNoteSection || !currentStepId) {
      console.error('Fonctions de sauvegarde non disponibles');
      return false;
    }
    
    try {
      const success = await updateStepNoteSection(currentStepId, noteData.sectionId, noteData.content);
      
      if (success) {
        console.log('Note sauvegardée avec succès');
        
        const notification = {
          id: Date.now(),
          type: 'manual_save',
          message: `Section "${noteData.sectionId}" mise à jour`,
          timestamp: new Date().toISOString()
        };
        
        setFeedbackNotifications(prev => [...prev.slice(-2), notification]);
        
        setTimeout(() => {
          setFeedbackNotifications(prev => prev.filter(n => n.id !== notification.id));
        }, 3000);
        
        generateSynthesisFromNotes();
      }
      
      return success;
    } catch (error) {
      console.error('Erreur sauvegarde note coordonnée:', error);
      return false;
    }
  }, [updateStepNoteSection, currentStepId, generateSynthesisFromNotes]);

  // Wrappers pour la gestion des résultats
  const handleUpdateStepResults = useCallback(async (stepId, newResult) => {
    console.log('Mise à jour résultats étape:', stepId);
    
    if (!localActionPlan) {
      console.error('Aucun plan d\'action local disponible');
      return false;
    }

    try {
      const updatedSteps = localActionPlan.steps.map(step => {
        if (step.id === stepId) {
          const updatedResults = [...(step.resultats || []), newResult];
          return {
            ...step,
            resultats: updatedResults,
            progression: Math.min((step.progression || 0) + 10, 100),
            points: (step.points || 0) + (newResult.points || 10)
          };
        }
        return step;
      });

      const updatedPlan = {
        ...localActionPlan,
        steps: updatedSteps
      };

      setLocalActionPlan(updatedPlan);

      if (saveStepResults) {
        const success = await saveStepResults(stepId, newResult);
        if (success) {
          console.log('Résultats sauvegardés en Firebase');
        }
      }

      setUserProgress(prev => ({
        ...prev,
        totalPoints: prev.totalPoints + (newResult.points || 10),
        completedActions: prev.completedActions + 1
      }));

      console.log('Résultats mis à jour avec succès');
      return true;
      
    } catch (error) {
      console.error('Erreur mise à jour résultats:', error);
      return false;
    }
  }, [localActionPlan, saveStepResults]);

  const handleCompleteAction = useCallback(async (actionId) => {
    console.log('Marquage action comme terminée:', actionId);
    
    try {
      setLocalWeeklyPlanning(prev => {
        return prev.map(week => ({
          ...week,
          elementaryActions: week.elementaryActions?.map(action =>
            action.id === actionId 
              ? { ...action, completed: true, completedAt: new Date().toISOString() }
              : action
          ) || []
        }));
      });

      const result = await handleUpdateElementaryAction(actionId, {
        completed: true,
        completedAt: new Date().toISOString()
      });

      if (result) {
        setUserProgress(prev => ({
          ...prev,
          completedActions: prev.completedActions + 1,
          totalPoints: prev.totalPoints + 15
        }));
      }

      return result;
    } catch (error) {
      console.error('Erreur completion action:', error);
      return false;
    }
  }, [handleUpdateElementaryAction]);

  const handleStepComplete = useCallback(async (stepId) => {
    console.log('Completion d\'étape demandée:', stepId);
    
    if (!updateStepStatus) {
      console.error('Fonction updateStepStatus non disponible');
      return false;
    }
    
    try {
      const success = await updateStepStatus(stepId, true);
      
      if (success) {
        console.log('Étape complétée avec mise à jour automatique de l\'étape courante');
        
        setUserProgress(prev => ({
          ...prev,
          totalPoints: prev.totalPoints + 50,
          completedActions: prev.completedActions + 1
        }));
      }
      
      return success;
    } catch (error) {
      console.error('Erreur completion étape:', error);
      return false;
    }
  }, [updateStepStatus]);

  // Utilitaires et status
  const getDataStatus = useMemo(() => {
    return {
      hasActionPlan: !!localActionPlan,
      hasWeeklyPlanning: localWeeklyPlanning.length > 0,
      hasCompetenceData: !!competenceData,
      hasSaveFunctions: !!(saveWeeklyPlanning && saveElementaryAction && deleteElementaryAction),
      hasStepFunctions: !!(changeCurrentStep && updateStepStatus && getCurrentStep),
      hasStepNotesFunctions: !!(saveStepNotes && updateStepNoteSection && getStepNotes),
      isConnected: connectionStatus === 'connected',
      isReady: isDataReady && !loading && !error,
      totalActions: localWeeklyPlanning.reduce((sum, week) => sum + (week.elementaryActions?.length || 0), 0),
      planningVersion,
      currentStepId,
      currentStepTitle: getCurrentStep()?.titre,
      hasCurrentStep: !!currentStepId,
      hasSynthesis: !!synthesisData?.hasContent
    };
  }, [
    localActionPlan, localWeeklyPlanning, competenceData, 
    saveWeeklyPlanning, saveElementaryAction, deleteElementaryAction,
    changeCurrentStep, updateStepStatus, getCurrentStep,
    saveStepNotes, updateStepNoteSection, getStepNotes,
    connectionStatus, isDataReady, loading, error, 
    planningVersion, currentStepId, synthesisData
  ]);

  // Initialisation des données sociales
  useEffect(() => {
    if (competenceData) {
      setSocialData({
        users: [
          {
            id: 'user1',
            name: 'Marie Laurent',
            expertise: 'Stratégie digitale',
            points: 2850,
            avatar: '👩‍💼',
            status: 'online'
          },
          {
            id: 'user2',
            name: 'Thomas Chen', 
            expertise: 'Gestion de projet',
            points: 3120,
            avatar: '👨‍💻',
            status: 'online'
          },
          {
            id: 'user3',
            name: 'Sophie Martin',
            expertise: 'Design UX/UI',
            points: 2650,
            avatar: '👩‍🎨',
            status: 'away'
          }
        ],
        discussions: [
          {
            id: 'disc1',
            title: 'Stratégies de croissance digitale',
            participants: 12,
            lastMessage: 'Il y a 5 minutes',
            category: 'Stratégie'
          }
        ],
        groups: [
          {
            id: 'group1',
            name: 'Entrepreneurs Tech',
            members: 245,
            category: 'Business',
            isJoined: false
          }
        ],
        events: [
          {
            id: 'event1',
            title: 'Workshop: Plan d\'action efficace',
            date: new Date(Date.now() + 86400000).toISOString(),
            participants: 28,
            maxParticipants: 50
          }
        ],
        collaborations: []
      });
    }
  }, [competenceData]);

  // Debug et logs
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const logData = {
        competenceId,
        connectionStatus,
        loading,
        error: !!error,
        isDataReady,
        hasActionPlanFromHook: !!actionPlan,
        hasLocalActionPlan: !!localActionPlan,
        actionPlanSteps: localActionPlan?.steps?.length || 0,
        currentStepId,
        currentStepTitle: getCurrentStep()?.titre,
        currentStepCompleted: getCurrentStep()?.termine,
        hasCurrentStep: !!currentStepId,
        weeklyPlanningFromHook: competenceData?.weeklyPlanning?.length || 0,
        localWeeklyPlanningLength: localWeeklyPlanning.length,
        planningVersion,
        stepNotesCount: Object.keys(competenceData?.stepNotes || {}).length,
        stepNotesStats: getStepNotesStats?.(),
        hasSynthesis: !!synthesisData?.hasContent,
        synthesisElementCount: synthesisData?.counts?.total || 0,
        saveFunctions: {
          saveWeeklyPlanning: typeof saveWeeklyPlanning,
          saveElementaryAction: typeof saveElementaryAction,
          deleteElementaryAction: typeof deleteElementaryAction,
          changeCurrentStep: typeof changeCurrentStep,
          updateStepStatus: typeof updateStepStatus,
          getCurrentStep: typeof getCurrentStep,
          saveStepNotes: typeof saveStepNotes,
          updateStepNoteSection: typeof updateStepNoteSection,
          getStepNotes: typeof getStepNotes
        },
        dataStatus: getDataStatus,
        updatedRideauxCount: updatedRideaux.size,
        timestamp: new Date().toISOString()
      };

      console.log('[AladinnWorkspace] État complet:', logData);
    }
  }, [
    competenceId, connectionStatus, loading, error, isDataReady, 
    actionPlan, localActionPlan, competenceData?.weeklyPlanning, localWeeklyPlanning, 
    planningVersion, saveWeeklyPlanning, saveElementaryAction, deleteElementaryAction, 
    saveStepNotes, updateStepNoteSection, getStepNotes, getStepNotesStats,
    getDataStatus, currentStepId, getCurrentStep, changeCurrentStep, updateStepStatus,
    synthesisData, updatedRideaux
  ]);

  useEffect(() => {
    window.debugAladinnData = debugDataState;
    return () => delete window.debugAladinnData;
  }, [debugDataState]);

  // Gestion des ressources
  const handleSaveChat = useCallback(async (chatMessages, conversationContext) => {
    try {
      const success = await saveChatData(chatMessages, conversationContext);
      if (success) {
        console.log("Conversation sauvegardée avec succès");
      }
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
    }
  }, [saveChatData]);

  useEffect(() => {
    return () => {
      console.log("Nettoyage des ressources Aladinn Workspace...");
      chatContextManager.cleanup();
      clearCache();
      console.log("Nettoyage terminé");
    };
  }, [clearCache]);

  // Fonctions de gestion d'erreur
  const handleRetry = () => {
    console.log("Tentative de reconnexion...");
    window.location.reload();
  };

  const handleGoBack = () => {
    console.log("Retour à la page précédente");
    window.history.back();
  };

  // Composant d'affichage des notifications simplifiées
  const NotificationPanel = () => {
    if (feedbackNotifications.length === 0) return null;

    return (
      <div className="fixed bottom-6 right-6 z-50 space-y-2">
        {feedbackNotifications.map((notification) => (
          <div
            key={notification.id}
            className={`max-w-sm p-4 rounded-lg shadow-lg border animate-slide-in-right ${
              notification.type === 'guided_sync' 
                ? 'bg-green-50 border-green-200' 
                : notification.type === 'manual_save'
                ? 'bg-blue-50 border-blue-200'
                : 'bg-purple-50 border-purple-200'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                notification.type === 'guided_sync' 
                  ? 'bg-green-100' 
                  : notification.type === 'manual_save'
                  ? 'bg-blue-100'
                  : 'bg-purple-100'
              }`}>
                {notification.type === 'guided_sync' && <CheckCircle size={14} className="text-green-600" />}
                {notification.type === 'manual_save' && <FileText size={14} className="text-blue-600" />}
              </div>
              
              <div className="flex-1">
                <div className={`text-sm font-medium ${
                  notification.type === 'guided_sync' 
                    ? 'text-green-800' 
                    : notification.type === 'manual_save'
                    ? 'text-blue-800'
                    : 'text-purple-800'
                }`}>
                  Synchronisation réussie
                </div>
                <div className={`text-xs mt-1 ${
                  notification.type === 'guided_sync' 
                    ? 'text-green-700' 
                    : notification.type === 'manual_save'
                    ? 'text-blue-700'
                    : 'text-purple-700'
                }`}>
                  {notification.message}
                </div>
              </div>
              
              <button
                onClick={() => setFeedbackNotifications(prev => prev.filter(n => n.id !== notification.id))}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const StatusIndicator = () => {
    return (
      <div className="flex items-center gap-2 px-3 py-1 bg-white rounded-full border border-gray-200 shadow-sm">
        <div className={`w-2 h-2 rounded-full ${getDataStatus.isReady ? 'bg-green-500' : 'bg-gray-400'}`} />
        <span className="text-xs font-medium text-gray-700">
          {getDataStatus.isReady ? 'Synchronisé' : 'En cours...'}
        </span>
        {synthesisData?.hasContent && (
          <div className="flex items-center gap-1 ml-2">
            <div className="w-2 h-2 rounded-full bg-blue-500" />
            <span className="text-xs text-gray-600">{synthesisData.counts?.total || 0}</span>
          </div>
        )}
      </div>
    );
  };

  // Rendu conditionnel
  if (loading) {
    return <LoadingComponent />;
  }

  if (error) {
    return (
      <ErrorComponent 
        error={error} 
        onRetry={handleRetry} 
        onGoBack={handleGoBack} 
      />
    );
  }

  if (!competenceData && connectionStatus === 'connected') {
    return (
      <ErrorComponent 
        error="Données de compétence non trouvées. Vérifiez l'ID de la compétence." 
        onRetry={handleRetry} 
        onGoBack={handleGoBack} 
      />
    );
  }

  // Interface principale
  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      <style>{fontStyles}</style>
      <style>{additionalStyles}</style>
      
      {/* ✨ NOUVEAU - Modal de bienvenue */}
      {showWelcome && (
        <WorkspaceWelcome
          onClose={handleCloseWelcome}
          onNavigate={handleNavigateFromWelcome}
          competenceData={competenceData}
          currentView={workspaceView}
        />
      )}
      
      {/* Header responsive unifié */}
      <ResponsiveHeader
        workspaceView={workspaceView}
        onViewChange={handleViewChange}
        onQuickAccess={handleQuickAccess}
        onOpenModal={handleOpenModal}
        userObjective={userObjective}
        actionPlan={localActionPlan}
        savedSources={savedSources}
        currentStepId={currentStepId}
        hasNotifications={hasNotifications}
        notesHasUpdates={updatedRideaux.size > 0}
        explorationStats={explorationStats}
        syncStatusComponent={<StatusIndicator />}
      />

      {/* Contenu principal du workspace */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full px-1 sm:px-2 py-1 sm:py-2 workspace-container">
          
          {/* Vue Chat */}
          {workspaceView === 'chat' && (
            <div className="h-full flex flex-col bg-white rounded-lg border border-gray-200 mx-0 sm:mx-2 workspace-content">
              {/* ✨ NOUVEAU - Bandeau d'info contextuel pour le Chat */}
              {isFirstTimeUser && !showWelcome && (
                <div className="p-3 sm:p-4">
                  <WorkspaceInfoBanner 
                    viewId="chat" 
                    contextId={currentChatMode === 'guided' ? 'progressionGuidee' : 'conversationLibre'}
                  />
                </div>
              )}
              
              <div className="flex-1 min-h-0 flex flex-col">
                <div className="flex-1 min-h-0">
                  <ChatInterface
                    userObjective={userObjective}
                    searchObjective={searchObjective}
                    savedSources={savedSources}
                    actionPlan={localActionPlan}
                    currentStepId={currentStepId}
                    competenceData={competenceData} 
                    changeCurrentStep={changeCurrentStep}
                    onUpdateStepResults={handleUpdateStepResults}
                    userProgress={userProgress}
                    chatContextManager={chatContextManager}
                    disableModals={true}
                    updateStepNoteSection={updateStepNoteSection}
                    getStepNotes={getStepNotes}
                    saveStepSynthesis={saveStepSynthesis}
                    getStepSynthesis={getStepSynthesis}
                    onNavigateToNotes={handleNavigateToNotes}
                    onGuidedInteraction={handleGuidedInteraction}
                    onNotesSaved={handleNotesSaved}
                    onChatModeChange={setCurrentChatMode}
                  />
                </div>
              </div>
              
              {/* ✨ NOUVEAU - Bouton d'aide flottant pour le Chat (desktop uniquement) */}
              <div className="hidden lg:block">
                <WorkspaceHelpFAB 
                  viewId="chat" 
                  contextId={currentChatMode === 'guided' ? 'progressionGuidee' : 'conversationLibre'}
                />
              </div>
            </div>
          )}

          {/* Vue Planning */}
          {workspaceView === 'planning' && (
            <div className="h-full overflow-auto mx-0 sm:mx-2">
              <div className="bg-white rounded-lg border border-gray-200 p-2 sm:p-4 h-full workspace-content">
                {/* ✨ NOUVEAU - Bandeau d'info contextuel pour Planning */}
                {isFirstTimeUser && !showWelcome && (
                  <WorkspaceInfoBanner viewId="planning" />
                )}
                
                {getDataStatus.isReady && getDataStatus.hasSaveFunctions && getDataStatus.hasStepFunctions ? (
                  <PlanningManager 
                    actionPlan={localActionPlan}
                    weeklyPlanning={localWeeklyPlanning}
                    currentStepId={currentStepId}
                    getCurrentStep={getCurrentStep}
                    onCompleteAction={handleCompleteAction}
                    onUpdatePlanning={handleUpdatePlanning}
                    onUpdateElementaryAction={handleUpdateElementaryAction}
                    onDeleteElementaryAction={handleDeleteElementaryAction}
                    onStepComplete={handleStepComplete}
                    onStepChange={changeCurrentStep}
                    competenceData={competenceData}
                    userObjective={userObjective}
                    getStepNotes={getStepNotes}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full p-4">
                    <div className="text-center max-w-md w-full">
                      <AlertCircle className="h-8 w-8 sm:h-12 sm:w-12 text-orange-500 mx-auto mb-4" />
                      <h3 className="section-subtitle text-gray-900 mb-2">
                        Planning en cours de chargement
                      </h3>
                      <p className="chat-text text-gray-600 mb-4">
                        Synchronisation des données en cours...
                      </p>
                    </div>
                  </div>
                )}
                
                {/* ✨ NOUVEAU - Bouton d'aide flottant pour Planning (desktop uniquement) */}
                <div className="hidden lg:block">
                  <WorkspaceHelpFAB viewId="planning" />
                </div>
              </div>
            </div>
          )}

          {/* Vue Notes avec badges */}
          {workspaceView === 'notes' && (
            <div className="h-full overflow-auto mx-0 sm:mx-2">
              <div className="bg-white rounded-lg border border-gray-200 h-full workspace-content">
                {/* ✨ NOUVEAU - Bandeau d'info contextuel pour Notes */}
                {isFirstTimeUser && !showWelcome && (
                  <div className="p-3 sm:p-4">
                    <WorkspaceInfoBanner viewId="notes" contextId="general" />
                  </div>
                )}
                
                {getDataStatus.isReady && getDataStatus.hasStepNotesFunctions ? (
                  <NotesManager 
                    actionPlan={localActionPlan}
                    currentStepId={currentStepId}
                    getCurrentStep={getCurrentStep}
                    competenceData={competenceData}
                    onSaveNote={handleSaveStepNoteEnhanced}
                    userObjective={userObjective}
                    userProgress={userProgress} 
                    notesTimestamp={notesTimestamp}
                    updatedRideaux={updatedRideaux}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full p-4">
                    <div className="text-center max-w-md w-full">
                      <BookOpen className="h-8 w-8 sm:h-12 sm:w-12 text-purple-500 mx-auto mb-4" />
                      <h3 className="section-subtitle text-gray-900 mb-2">
                        Notes en cours de chargement
                      </h3>
                      <p className="chat-text text-gray-600 mb-4">
                        Synchronisation des données en cours...
                      </p>
                    </div>
                  </div>
                )}
                
                {/* ✨ NOUVEAU - Bouton d'aide flottant pour Notes (desktop uniquement) */}
                <div className="hidden lg:block">
                  <WorkspaceHelpFAB viewId="notes" contextId="general" />
                </div>
              </div>
            </div>
          )}

          {/* Vue Analytics */}
          {workspaceView === 'analytics' && (
            <div className="h-full overflow-auto mx-0 sm:mx-2">
              <div className="bg-white rounded-lg border border-gray-200 p-2 sm:p-4 h-full workspace-content">
                {/* ✨ NOUVEAU - Bandeau d'info contextuel pour Analytics */}
                {isFirstTimeUser && !showWelcome && (
                  <WorkspaceInfoBanner viewId="analytics" />
                )}
                
                <ResultsManager 
                  actionPlan={localActionPlan}
                  weeklyPlanning={localWeeklyPlanning}
                  userProgress={userProgress}
                  currentStepId={currentStepId}
                  getCurrentStep={getCurrentStep}
                  onUpdateStepResults={handleUpdateStepResults}
                  onCompleteAction={handleCompleteAction}
                  onUpdatePlanning={handleUpdatePlanning}
                  onUpdateElementaryAction={handleUpdateElementaryAction}
                  onDeleteElementaryAction={handleDeleteElementaryAction}
                  onStepComplete={handleStepComplete}
                  onStepChange={changeCurrentStep}
                  competenceData={competenceData}
                  conversationHistory={[]}
                />
                
                {/* ✨ NOUVEAU - Bouton d'aide flottant pour Analytics (desktop uniquement) */}
                <div className="hidden lg:block">
                  <WorkspaceHelpFAB viewId="analytics" />
                </div>
              </div>
            </div>
          )}

          {/* Vue Social */}
          {workspaceView === 'social' && (
            <div className="h-full overflow-auto mx-0 sm:mx-2">
              <div className="h-full workspace-content">
                <SocialCommunityPanel 
                  socialData={socialData}
                  userObjective={userObjective}
                  actionPlan={localActionPlan}
                  userProgress={userProgress}
                  currentStepId={currentStepId}
                  getCurrentStep={getCurrentStep}
                  onSocialInteraction={(userId, action) => {
                    console.log('Interaction sociale:', userId, action);
                  }}
                  onJoinGroup={(groupId) => {
                    console.log('Rejoindre groupe:', groupId);
                    setSocialData(prev => ({
                      ...prev,
                      groups: prev.groups.map(group =>
                        group.id === groupId
                          ? { ...group, isJoined: true, members: group.members + 1 }
                          : group
                      )
                    }));
                  }}
                  onJoinEvent={(eventId) => {
                    console.log('Participer événement:', eventId);
                    setSocialData(prev => ({
                      ...prev,
                      events: prev.events.map(event =>
                        event.id === eventId
                          ? { ...event, participants: event.participants + 1, isRegistered: true }
                          : event
                      )
                    }));
                  }}
                  onStartCollaboration={(collaborationData) => {
                    console.log('Démarrer collaboration:', collaborationData);
                    const newCollaboration = {
                      id: `collab_${Date.now()}`,
                      title: collaborationData.title,
                      participants: [collaborationData.partnerId],
                      createdAt: new Date().toISOString(),
                      status: 'active'
                    };
                    setSocialData(prev => ({
                      ...prev,
                      collaborations: [...prev.collaborations, newCollaboration]
                    }));
                  }}
                />
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Modals centralisées */}
      <ObjectiveModal 
        show={showObjectiveModal}
        onClose={() => setShowObjectiveModal(false)}
        userObjective={userObjective}
        searchObjective={searchObjective}
        chatMessages={[]}
        savedSources={savedSources}
        actionPlan={localActionPlan}  
        currentStepId={currentStepId}  
        onStepChange={changeCurrentStep}
        onStepComplete={handleStepComplete}  
        forceUpdate={0}
        competenceData={competenceData}
        getStepNotes={getStepNotes}
      />
      
      <ActionPlanModal 
        show={showActionPlanModal}
        onClose={() => setShowActionPlanModal(false)}
        actionPlan={localActionPlan}
        currentStepId={currentStepId}
        onStepChange={changeCurrentStep}
        onStepComplete={handleStepComplete}
        forceUpdate={0}
      />
      
      <SourcesModal 
        show={showSourcesModal}
        onClose={() => setShowSourcesModal(false)}
        savedSources={savedSources || []}
      />
      
      {/* 🆕 NOUVEAU - Modal d'exploration */}
      <ExplorationModal 
        show={showExplorationModal}
        onClose={() => setShowExplorationModal(false)}
        userId={userId}
        userObjective={userObjective}
        competenceData={competenceData}
        onDocumentUploaded={() => {
          console.log('Document téléchargé - Mettre à jour les statistiques');
          // TODO: Mettre à jour les statistiques d'exploration
        }}
        onConceptSelected={(concept) => {
          console.log('Concept sélectionné:', concept);
          // TODO: Naviguer vers la vue concept
        }}
        onUseConceptInChat={(concept) => {
          console.log('Concept utilisé dans chat:', concept);
          // TODO: Injecter le concept dans le champ de chat
        }}
      />

      {/* Modal Paramètres */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full mx-4 p-4 sm:p-6 modal-content">
            <div className="flex justify-between items-center mb-4">
              <h3 className="section-subtitle">Paramètres</h3>
              <button 
                onClick={() => setShowSettingsModal(false)}
                className="text-gray-400 hover:text-gray-600 p-2 touch-target"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block context-text text-gray-700 mb-2">
                  Synchronisation automatique
                </label>
                <div className="space-y-3">
                  <label className="flex items-center p-2">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 touch-target" 
                      checked={getDataStatus.isReady}
                      disabled
                    />
                    <span className="ml-3 context-text text-gray-600">
                      Sync Chat → Notes {getDataStatus.isReady ? '(Actif)' : '(Inactif)'}
                    </span>
                  </label>
                  <label className="flex items-center p-2">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50 touch-target" 
                      defaultChecked 
                    />
                    <span className="ml-3 context-text text-gray-600">Notifications de sync</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block context-text text-gray-700 mb-2">
                  Vue par défaut
                </label>
                <select 
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-3 touch-target"
                  value={workspaceView}
                  onChange={(e) => setWorkspaceView(e.target.value)}
                >
                  <option value="chat">Chat</option>
                  <option value="planning">Planning</option>
                  <option value="notes">Notes</option>
                  <option value="analytics">Analytics</option>
                </select>
              </div>
              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={handleShowWelcome}
                  className="w-full px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors flex items-center justify-center gap-2"
                >
                  <HelpCircle size={16} />
                  <span>Revoir le guide de bienvenue</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notifications */}
      <NotificationPanel />

      <div 
        id="toast-container" 
        className="fixed top-4 right-2 sm:right-4 z-50 space-y-2 max-w-sm"
        style={{ pointerEvents: 'none' }}
      >
      </div>
    </div>
  );
};
 
export default AladinnContextualChat;
